import { DateTime } from 'luxon';
import { PetProps } from '@barkinglabs/nano-shared';
import { gqlTypes } from '@/types/gqlTypes';
import dogPlaceholderImage from '../../../../assets/images/dog-placeholder.png';

export type Pet = gqlTypes.basePet | PetProps;

const pluralize = (value: number, unit: string) => `${value} ${unit}${value > 1 ? 's' : ''}`;

export const getPhotoUrl = (pet: Pet) => {
  let photoUrl;
  if ('photos' in pet) {
    photoUrl = pet.photos.first?.image.fullSize;
  } else if ('photoUrl' in pet) {
    photoUrl = pet.photoUrl;
  }

  return photoUrl ?? dogPlaceholderImage;
};

export const getStatusText = (status?: string) => {
  let statusText = `Active`;
  if (status === 'pending') {
    statusText = 'Transfer Pending';
  } else if (status === 'inactive') {
    statusText = 'Inactive';
  }

  return statusText;
};

export const getLastUpdatedText = (pet: Pet) => {
  if ('updatedAt' in pet) {
    return `Last updated ${DateTime.fromISO(pet.updatedAt).toFormat('M/d/yy')}`;
  }
  return 'Last updated: No data';
};

export const getBreedText = (pet: Pet) => {
  let breedText;
  if ('breed' in pet) {
    breedText = pet.breed?.name;
  } else if ('breedName' in pet) {
    breedText = pet.breedName;
  }

  return breedText ?? 'No data';
};

export const getAgeText = (pet: Pet) => {
  const day = pet.dayOfBirth ?? 1; // Default to 1st of month when day is not specified
  const petDateOfBirth = DateTime.fromFormat(`${pet.monthOfBirth}/${day}/${pet.yearOfBirth}`, 'M/d/y');

  const now = DateTime.now();
  const diff = now.diff(petDateOfBirth, ['years', 'months', 'days']).toObject();

  const years = Math.floor(diff.years ?? 0);
  const months = Math.floor((diff.months ?? 0) % 12);
  const days = Math.floor(diff.days ?? 0);

  // If age is less than 1 month, use days
  if (years === 0 && months === 0) {
    return pluralize(days, 'day');
  }

  const parts: string[] = [];
  if (years > 0) parts.push(pluralize(years, 'year'));
  if (months > 0) parts.push(pluralize(months, 'month'));

  return parts.join(' ');
};

export const getGenderText = (pet: Pet) => {
  return pet.gender.slice(0, 1).toUpperCase() + pet.gender.slice(1).toLowerCase();
};

export const getSpeciesText = (pet: Pet) => {
  return pet.species ? pet.species.slice(0, 1).toUpperCase() + pet.species.slice(1).toLowerCase() : 'No data';
};
